import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { type FC, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemRoundedCheckbox } from "../../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useCreateJob } from "../../../../hooks/useJobs";
import { backendService } from "../../../../services/backend.service";
import type { CertificationJobMapping, Job } from "../../../../utils/models";

function formatRoleLabel(rs: string): string {
	return rs.includes(" ") ? rs.replace(/\b\w/g, (c) => c.toUpperCase()) : rs.toUpperCase();
}

function toTitleCase(s: string): string {
	return s.replace(/\b([a-zA-Z])/g, (c) => c.toUpperCase());
}

// Lookahead requires at least one letter so separator-only names ("-", " - ") are rejected.
// Mirrors JOB_DISPLAY_NAME_PATTERN in backend/model.py — the two must stay in sync.
const JOB_NAME_PATTERN = /^(?=.*[a-zA-Z])[a-zA-Z\s-]+$/;

type Props = {
	open: boolean;
	onClose: () => void;
	onSuccess: (job: Job) => void;
	departmentId: string;
	enabledJobIds: string[];
	existingJobNames: string[];
};

const CreateJobDialog: FC<Props> = ({ open, onClose, onSuccess, departmentId, enabledJobIds, existingJobNames }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const queryClient = useQueryClient();
	const createJob = useCreateJob();
	const certDropdownId = useId();

	const [displayName, setDisplayName] = useState("");
	const [selectedRoleStrings, setSelectedRoleStrings] = useState<string[]>([]);
	const [excludeFromPpd, setExcludeFromPpd] = useState(true);
	const [submitTriggered, setSubmitTriggered] = useState(false);
	const [certMapping, setCertMapping] = useState<CertificationJobMapping[]>([]);

	// Reset state when dialog opens/closes.
	useEffect(() => {
		if (!open) return;
		setDisplayName("");
		setSelectedRoleStrings([]);
		// DEFAULT: true is intentional per product spec — most new jobs should exclude from PPD.
		setExcludeFromPpd(true);
		setSubmitTriggered(false);
		setCertMapping([]);
		backendService.getDepartmentCertJobMapping(departmentId).then((res) => setCertMapping(res.data));
	}, [open, departmentId]);

	// All role strings for this department — same set shown in the Certification → Job Mapping table.
	// Certs mapped to a currently-enabled job are disabled, matching the existing table behavior.
	const enabledJobIdSet = useMemo(() => new Set(enabledJobIds), [enabledJobIds]);
	const certItems = useMemo(
		() =>
			certMapping
				.map((m) => ({
					value: m.roleString,
					label: formatRoleLabel(m.roleString),
					selectedLabel: formatRoleLabel(m.roleString),
					disabled: !!m.jobs[0] && enabledJobIdSet.has(m.jobs[0].id),
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[certMapping, enabledJobIdSet],
	);

	const trimmed = displayName.trim();
	const isNameEmpty = trimmed === "";
	const isNameValid = JOB_NAME_PATTERN.test(trimmed);
	const inputSlug = trimmed.toLowerCase().replace(/[\s-]+/g, "_");
	const isNameConflict = !isNameEmpty && isNameValid && existingJobNames.includes(inputSlug);

	const nameValidation = useMemo(
		() => ({
			validationCallback: (value: string) => {
				if (!value.trim()) return false;
				if (!JOB_NAME_PATTERN.test(value.trim())) return false;
				const slug = value
					.trim()
					.toLowerCase()
					.replace(/[\s-]+/g, "_");
				return !existingJobNames.includes(slug);
			},
			helperText: {
				invalid: isNameEmpty ? t("create.job.name.required") : isNameConflict ? t("create.job.name.conflict") : t("create.job.name.invalid"),
			},
			performValidation: submitTriggered || (!isNameEmpty && (!isNameValid || isNameConflict)) ? ("always" as const) : ("onlyAfterInitialChange" as const),
			showValidWhenValid: "never" as const,
		}),
		[t, isNameEmpty, isNameValid, isNameConflict, submitTriggered, existingJobNames],
	);

	const handleConfirm = async () => {
		setSubmitTriggered(true);
		if (isNameEmpty || !isNameValid || isNameConflict) return;

		track("create_job_dialog_confirm_clicked", { department_id: departmentId });

		// mutateAsync rejects on failure; .catch returns undefined so we can guard below.
		// TanStack Query still fires onError (snackbar) regardless of how we handle the promise.
		const newJob = await createJob
			.mutateAsync({
				displayName: toTitleCase(displayName.trim()),
				excludeFromPpd,
				deptId: departmentId,
				certRoleStrings: selectedRoleStrings,
			})
			.catch(() => undefined);
		if (!newJob) return;

		queryClient.invalidateQueries({ queryKey: ["certJobMapping", departmentId] });
		onSuccess(newJob);
		onClose();
	};

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("create.job.dialog.title")}
			confirm={t("save")}
			cancel={t("cancel")}
			confirmDisabled={createJob.isPending || isNameEmpty || !isNameValid || isNameConflict}
			confirmLoading={createJob.isPending}
			onConfirm={handleConfirm}
			dataTestId="create-job-dialog"
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: "420px" }}>
					<SystemValidationTextField
						label={t("create.job.name.label")}
						value={displayName}
						onChange={(e) => setDisplayName(e.target.value)}
						inputProps={{ onBlur: () => setDisplayName((prev) => toTitleCase(prev.trim())) }}
						required
						fullWidth
						autoFocus
						validation={nameValidation}
						submitTriggerred={submitTriggered}
						reserveHeightForHelperText
						dataTestId="create-job-name-input"
					/>

					<SystemDropdown
						label={t("create.job.certifications.label")}
						id={certDropdownId}
						multiple
						fullWidth
						showFilter
						items={certItems}
						selectedItems={selectedRoleStrings}
						onChangeMultiple={setSelectedRoleStrings}
						data-testid="create-job-certifications-dropdown"
					/>

					<SystemRoundedCheckbox
						checked={excludeFromPpd}
						onChange={setExcludeFromPpd}
						label={t("create.job.exclude.from.ppd.label")}
						data-testid="create-job-exclude-ppd-checkbox"
					/>
				</Box>
			}
		/>
	);
};

export default CreateJobDialog;
